import React, { useEffect, useId, useMemo, useState } from 'react';
import { useMaybeAuth, isEmailVerified } from '../auth/AuthContext';
import iconShow from '../resources/show.svg';
import iconHide from '../resources/hide.svg';
import slicerBro from '../resources/slicer-bro.webp';
import SHARED_CONST from '../../backAndFront/constant';
import { ROUTES } from '../util/routes';
import FormInput from './FormInput';
import ModalShell from './ModalShell';
import Toggle from './Toggle';
import './AuthModal.scss';

export default function AuthModal({
  isOpen,
  onClose,
  onExited,
  claimId: propClaimId,
  initialMode,
  resetToken: propResetToken,
  resetEmail: propResetEmail,
  onOpenAccount
}: {
  isOpen: boolean;
  onClose: () => void;
  onExited?: () => void;
  claimId?: string;
  initialMode?: 'login' | 'register' | 'forgot' | 'reset';
  resetToken?: string;
  resetEmail?: string;
  /** Hand over to the My Account dialog. Falls back to the page when not supplied. */
  onOpenAccount?: () => void;
}) {
  const auth = useMaybeAuth();

  // Get claimId from prop or URL query string
  const claimId = useMemo(() => {
    if (propClaimId) return propClaimId;
    try {
      return new URLSearchParams(window.location.search).get('claim') || undefined;
    } catch (_e) {
      return undefined;
    }
  }, [propClaimId]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'reset'>(() => initialMode || 'login');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const passwordId = useId();

  // Don't render (or crash) when AuthProvider isn't mounted (e.g. unit tests).
  if (!auth) return null;

  const {
    user,
    isLoading,
    login,
    register,
    requestPasswordReset,
    confirmPasswordReset
  } = auth;

  useEffect(() => {
    if (!isOpen) return;
    setErr(null);
    setInfo(null);
    // Cosmetic reset for each open.
    setShowPassword(false);
    setPasswordConfirm('');
    // Start from the requested mode (reset deep-link) or default to login.
    setMode(initialMode || 'login');
    // Pre-fill email from reset link
    if (initialMode === 'reset' && propResetEmail) {
      setEmail(propResetEmail);
    }
  }, [isOpen, initialMode, propResetEmail]);

  const handleRegister = async() => {
    setErr(null);
    setInfo(null);
    if (password.length < SHARED_CONST.AUTH_PASSWORD_MIN_LEN) {
      setErr(`Register failed: password must be at least ${SHARED_CONST.AUTH_PASSWORD_MIN_LEN} characters.`);
      return;
    }
    try {
      await register(name || email.split('@')[0], email, password, claimId);
      setPassword('');
      // After registration, user is logged in via session
      // If claimId was provided, AuthContext will handle redirect
      if (!claimId) {
        setInfo('Registered successfully!');
      }
    } catch (_e) {
      if (_e instanceof Error) {
        setErr(_e.message);
        return;
      }
      setErr('Register failed.');
    }
  };

  const handleLogin = async() => {
    setErr(null);
    setInfo(null);
    if (password.length < SHARED_CONST.AUTH_PASSWORD_MIN_LEN) {
      setErr(`Login failed: password must be at least ${SHARED_CONST.AUTH_PASSWORD_MIN_LEN} characters.`);
      return;
    }
    try {
      await login(email, password, claimId, rememberMe);
      setPassword('');
      // Signed in: get out of the way. Account actions live on the My Account page.
      onClose();
      // If claimId was provided, AuthContext will handle redirect
    } catch (_e) {
      if (_e instanceof Error && _e.message === 'EMAIL_NOT_VERIFIED') {
        setErr('Login blocked: please verify your email first.');
        return;
      }
      setErr('Login failed.');
    }
  };

  const handleRequestReset = async() => {
    setErr(null);
    setInfo(null);
    try {
      await requestPasswordReset(email);
      setInfo('Password reset link sent. Please check your email.');
    } catch (_e) {
      setErr('Password reset request failed.');
    }
  };

  const handleConfirmReset = async() => {
    setErr(null);
    setInfo(null);
    if (password.length < SHARED_CONST.AUTH_PASSWORD_MIN_LEN) {
      setErr(`Password must be at least ${SHARED_CONST.AUTH_PASSWORD_MIN_LEN} characters.`);
      return;
    }
    if (password !== passwordConfirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (!propResetToken || !email) {
      setErr('Invalid reset link. Please request a new one.');
      return;
    }
    try {
      await confirmPasswordReset(email, propResetToken, password);
      setPassword('');
      setPasswordConfirm('');
      setInfo('Password reset successfully! You can now log in.');
      setMode('login');
    } catch (_e) {
      setErr('Password reset failed. The link may have expired.');
    }
  };

  const renderLoginForm = () => {
    const onSubmitLogin = (e: React.FormEvent) => {
      e.preventDefault();
      if (email && password && !isLoading) {
        handleLogin();
      }
    };

    return (
      <form className="auth-form" onSubmit={ onSubmitLogin }>
        <div className="auth-form-hero">
          <h1 className="aitch-one auth-form-hero-title">Hi there!</h1>
          <div className="auth-form-hero-subtitle">
            Welcome to Image Slicer, so happy to see you!
          </div>
        </div>

        <div className="auth-form-switch">
          <span>Not a member?</span>{' '}
          <button type="button" className="green-link-btn" onClick={ () => setMode('register') }>
            Sign Up!
          </button>
        </div>

        <FormInput
          label="Email"
          value={ email }
          onChange={ setEmail }
          autoComplete="email"
          placeholder="Email address"
        />

        <div className="auth-field">
          <label htmlFor={ passwordId } className="auth-field-label">Password</label>
          <div className="auth-input-with-icon">
            <input
              id={ passwordId }
              className="commanding-input"
              value={ password }
              onChange={ (e) => setPassword(e.target.value) }
              type={ showPassword ? 'text' : 'password' }
              autoComplete="current-password"
              placeholder="Enter password"
            />
            <button
              type="button"
              className="auth-icon-btn"
              aria-label={ showPassword ? 'Hide password' : 'Show password' }
              onClick={ () => setShowPassword(v => !v) }
            >
              <img
                className="auth-icon"
                src={ showPassword ? iconHide : iconShow }
                alt=""
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <Toggle
          id="cbRememberMe"
          checked={ rememberMe }
          onChange={ setRememberMe }
          label="Remember me"
          className="auth-remember-row"
          labelClassName="auth-remember-label"
          showStatus={ false }
        />

        <button
          type="submit"
          className="primary-btn"
          disabled={ !email || !password || isLoading }
        >
          Login
        </button>

        <button type="button" className="green-link-btn auth-forgot" onClick={ () => setMode('forgot') }>
          Forgot Password?
        </button>
      </form>
    );
  };

  return (
    <ModalShell
      isOpen={ isOpen }
      onClose={ onClose }
      onExited={ onExited }
      label="Authentication"
      overlayClassName="auth-modal-overlay"
      className="auth-modal"
    >
      <div className="auth-modal-left" aria-hidden="true">
          <img className="auth-modal-left-image" src={ slicerBro } alt="" />
        </div>
        <div className="auth-modal-right">
          {isLoading ? (
            <div className="auth-modal-note">Loading…</div>
          ) : (user && isOpen) ? (
            // Shown only right after signing up. Logging in CLOSES the dialog instead — but closing
            // takes a 300ms fade, and `user` is already set by then, so without the `isOpen` guard
            // this branch rendered during the exit: measured at ~325ms of "You're signed in as …"
            // flashing up in place of the form the user was looking at. A closing dialog keeps
            // showing what it was showing.
            //
            // Everything about the account — verification, log out, delete — lives on My Account.
            <>
              <div className="auth-modal-note">
                <div>You’re signed in as {user.email}.</div>
                {!isEmailVerified(user) && <div>Check your inbox to verify your email.</div>}
              </div>
              <div className="auth-modal-actions">
                { /* Opens the account DIALOG over whatever you were doing. It navigated to
                     /settings until now, which for someone who had just signed up mid-edit threw
                     away the upload they were working on. The page stays as the fallback (and for
                     Stripe's portal return), so this still works if no handler is supplied. */ }
                <button
                  type="button"
                  onClick={ onOpenAccount ?? (() => window.location.assign(ROUTES.SETTINGS)) }
                >
                  Go to My Account
                </button>
              </div>
            </>
          ) : (
            <>
              {mode === 'login' ? renderLoginForm() : null}

              {mode !== 'login' && (
                <div className="auth-modal-header">
                  <div className="auth-modal-title">
                    {mode === 'register' ? 'Create account' : mode === 'reset' ? 'Set new password' : 'Reset password'}
                  </div>
                </div>
              )}

              {mode === 'register' && (
                <form
                  className="auth-form"
                  onSubmit={ (e) => {
                    e.preventDefault();
                    if (name && email && password) handleRegister();
                  } }
                >
                  <FormInput
                    label="Name"
                    value={ name }
                    onChange={ setName }
                    autoComplete="name"
                    placeholder="Your name"
                  />

                  <FormInput
                    label="Email"
                    value={ email }
                    onChange={ setEmail }
                    autoComplete="email"
                  />

                  <FormInput
                    label="Password"
                    value={ password }
                    onChange={ setPassword }
                    type="password"
                    autoComplete="new-password"
                  />

                  <div className="auth-btn-row">
                    <button type="submit" disabled={ !name || !email || !password }>
                      Register
                    </button>
                    <button type="button" onClick={ () => setMode('login') }>Back to login</button>
                  </div>
                </form>
              )}

              {mode === 'forgot' && (
                <form
                  className="auth-form"
                  onSubmit={ (e) => {
                    e.preventDefault();
                    if (email) handleRequestReset();
                  } }
                >
                  <FormInput
                    label="Email"
                    value={ email }
                    onChange={ setEmail }
                    autoComplete="email"
                    placeholder="Enter your email address"
                  />

                  <div className="auth-btn-row">
                    <button type="submit" disabled={ !email || isLoading }>
                      Send reset link
                    </button>
                    <button type="button" onClick={ () => setMode('login') }>Back to login</button>
                  </div>
                </form>
              )}

              {mode === 'reset' && (
                <form
                  className="auth-form"
                  onSubmit={ (e) => {
                    e.preventDefault();
                    if (email && password && passwordConfirm) handleConfirmReset();
                  } }
                >
                  <FormInput
                    label="Email"
                    value={ email }
                    onChange={ setEmail }
                    autoComplete="email"
                    placeholder="Email address"
                  />

                  <FormInput
                    label="New password"
                    value={ password }
                    onChange={ setPassword }
                    type="password"
                    autoComplete="new-password"
                    placeholder="Enter new password"
                  />

                  <FormInput
                    label="Confirm password"
                    value={ passwordConfirm }
                    onChange={ setPasswordConfirm }
                    type="password"
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                  />

                  <div className="auth-btn-row">
                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={ !email || !password || !passwordConfirm || isLoading }
                    >
                      Reset password
                    </button>
                    <button type="button" onClick={ () => setMode('login') }>Back to login</button>
                  </div>
                </form>
              )}
            </>
          )}

          {info && <div className="auth-modal-info">{info}</div>}
          {err && <div className="auth-modal-error">{err}</div>}

          <div className="auth-modal-legal">
            By continuing, you agree to our <a href={ ROUTES.TERMS }>Terms of Use</a> and <a href={ ROUTES.PRIVACY }>Privacy Policy</a>.
          </div>
        </div>
    </ModalShell>
  );
}
